import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { DataManager } from '../../core/data-manager';
import {
    GIORNI, formatDate, formatTime, optValue, optPayload, refPayload, umanizza, toDateKey
} from '../../lib/format';
import { Field, ConfirmModal, BoolPill, FormModal, messaggioErrore } from './common';
import Select from '../../components/Select';

const TIPI = [
    { value: 'evento_ricorrente', label: 'Evento ricorrente' },
    { value: 'festivita', label: 'Festività' },
    { value: 'manutenzione', label: 'Manutenzione' },
    { value: 'evento_condominiale', label: 'Evento condominiale' }
];

const RICORRENZE = [
    { value: 'nessuna', label: 'Nessuna (data singola)' },
    { value: 'settimanale', label: 'Settimanale' },
    { value: 'mensile', label: 'Mensile' },
    { value: 'annuale', label: 'Annuale' }
];

const VUOTO = {
    titolo: '', tipo: 'festivita', ricorrenza: 'nessuna', giorno_settimana: '1',
    data_inizio: '', data_fine: '', ora_inizio: '', ora_fine: '', note: '', attivo: 1
};

/** Riassunto leggibile della regola di ricorrenza. */
function descriviRicorrenza(b) {
    const ric = optValue(b.ricorrenza);
    if (ric === 'settimanale') {
        const g = parseInt(optValue(b.giorno_settimana), 10);
        return `Ogni ${GIORNI[g - 1] ? GIORNI[g - 1].toLowerCase() : 'settimana'}`;
    }
    if (ric === 'mensile') return 'Ogni mese, stesso giorno';
    if (ric === 'annuale') return 'Ogni anno, stessa data';
    return 'Data singola';
}

/**
 * Blocchi di una sala: festività, manutenzioni ed eventi condominiali
 * ricorrenti (es. il GAS del giovedì) che la rendono non prenotabile.
 *
 * Vivono nella pagina della sala, accanto alle tariffe, perche' appartengono
 * alla singola sala: chiusure e festivita' possono variare da una sala
 * all'altra, e il BookingEngine espande solo i blocchi della sala che sta
 * guardando.
 *
 * Senza orari il blocco copre l'intera giornata.
 */
export default function BlocchiSala({ sala, blocchi, onChanged }) {
    const [form, setForm] = useState(null);
    const [inModifica, setInModifica] = useState(null);
    const [saving, setSaving] = useState(false);
    const [daEliminare, setDaEliminare] = useState(null);
    const [working, setWorking] = useState(false);

    const ordinati = useMemo(
        () => [...blocchi].sort((a, b) => String(b.data_inizio).localeCompare(String(a.data_inizio))),
        [blocchi]
    );

    function aggiorna(campo, valore) {
        setForm(prev => ({ ...prev, [campo]: valore }));
    }

    function apriNuovo() {
        setInModifica(null);
        setForm({ ...VUOTO, data_inizio: toDateKey(new Date()) });
    }

    function apriModifica(b) {
        setInModifica(b);
        setForm({
            titolo: b.titolo || '',
            tipo: optValue(b.tipo) || 'festivita',
            ricorrenza: optValue(b.ricorrenza) || 'nessuna',
            giorno_settimana: String(optValue(b.giorno_settimana) || '1'),
            data_inizio: b.data_inizio || '',
            data_fine: b.data_fine || '',
            ora_inizio: b.ora_inizio ? formatTime(b.ora_inizio) : '',
            ora_fine: b.ora_fine ? formatTime(b.ora_fine) : '',
            note: b.note || '',
            attivo: parseInt(b.attivo, 10) === 1 ? 1 : 0
        });
    }

    async function salva(e) {
        e.preventDefault();
        e.stopPropagation();
        if (!form.titolo.trim()) {
            toast.error('Il titolo è obbligatorio.', { theme: 'colored' });
            return;
        }
        if (!form.data_inizio) {
            toast.error('Indica la data di inizio.', { theme: 'colored' });
            return;
        }
        if (form.data_fine && form.data_fine < form.data_inizio) {
            toast.error('La data di fine precede quella di inizio.', { theme: 'colored' });
            return;
        }
        const soloUnOrario = Boolean(form.ora_inizio) !== Boolean(form.ora_fine);
        if (soloUnOrario) {
            toast.error('Indica entrambi gli orari, oppure lasciali vuoti per bloccare l\'intera giornata.', { theme: 'colored' });
            return;
        }
        if (form.ora_inizio && form.ora_fine <= form.ora_inizio) {
            toast.error("L'ora di fine deve seguire quella di inizio.", { theme: 'colored' });
            return;
        }

        const payload = {
            titolo: form.titolo.trim(),
            tipo: optPayload(form.tipo),
            ricorrenza: optPayload(form.ricorrenza),
            // la sala non e' una scelta: e' quella da cui si e' aperto l'elenco
            idSala: refPayload(sala.id),
            data_inizio: form.data_inizio,
            data_fine: form.data_fine,
            ora_inizio: form.ora_inizio ? `${form.ora_inizio}:00` : '',
            ora_fine: form.ora_fine ? `${form.ora_fine}:00` : '',
            note: form.note,
            attivo: parseInt(form.attivo, 10)
        };
        // il giorno della settimana esiste solo per la ricorrenza settimanale:
        // inviarlo vuoto farebbe fallire la validazione delle option lato API
        if (form.ricorrenza === 'settimanale') {
            payload.giorno_settimana = optPayload(form.giorno_settimana);
        }

        setSaving(true);
        try {
            const res = inModifica
                ? await DataManager.updateItem('blocco', payload, inModifica.id)
                : await DataManager.insertItem('blocco', payload);
            if (res && res.success === 1) {
                toast.success(inModifica ? 'Blocco aggiornato.' : 'Blocco creato.', { theme: 'colored' });
                setForm(null);
                setInModifica(null);
                onChanged();
            }
            else {
                toast.error(messaggioErrore(res, 'Salvataggio non riuscito.'), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setSaving(false);
        }
    }

    async function elimina() {
        setWorking(true);
        try {
            const res = await DataManager.deleteItem('blocco', daEliminare.id);
            if (res && res.success === 1) {
                toast.success('Blocco eliminato.', { theme: 'colored' });
                setDaEliminare(null);
                onChanged();
            }
            else {
                toast.error(messaggioErrore(res), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setWorking(false);
        }
    }

    return (
        <div className="lv-subsection">
            <div className="lv-subsection-head">
                <h3 className="lv-subsection-title">Blocchi e festività</h3>
                <button type="button" className="lv-btn lv-btn-ghost lv-btn-sm" onClick={apriNuovo}>
                    <FontAwesomeIcon icon={['fas', 'plus']} className="me-2" />
                    Aggiungi blocco
                </button>
            </div>

            {ordinati.length === 0 ? (
                <div className="lv-note">
                    Nessun blocco impostato: questa sala è libera in tutti i suoi orari di apertura.
                </div>
            ) : (
                <div className="lv-table-wrap">
                    <table className="lv-table">
                        <thead>
                            <tr>
                                <th>Blocco</th>
                                <th>Ricorrenza</th>
                                <th>Validità</th>
                                <th>Orario</th>
                                <th>Stato</th>
                                <th>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ordinati.map(b => (
                                <tr key={b.id}>
                                    <td data-label="Blocco">
                                        <div className="lv-cell-strong">{b.titolo}</div>
                                        <div className="lv-cell-sub">{umanizza(optValue(b.tipo))}</div>
                                    </td>
                                    <td data-label="Ricorrenza">{descriviRicorrenza(b)}</td>
                                    <td data-label="Validità">
                                        <div className="lv-cell-strong">{formatDate(b.data_inizio)}</div>
                                        <div className="lv-cell-sub">
                                            {b.data_fine ? `fino al ${formatDate(b.data_fine)}` : 'senza scadenza'}
                                        </div>
                                    </td>
                                    <td data-label="Orario">
                                        {b.ora_inizio
                                            ? `${formatTime(b.ora_inizio)} – ${formatTime(b.ora_fine)}`
                                            : 'Tutto il giorno'}
                                    </td>
                                    <td data-label="Stato">
                                        <BoolPill value={b.attivo} labelOn="Attivo" labelOff="Non attivo" />
                                    </td>
                                    <td data-label="Azioni">
                                        <div className="lv-row-actions">
                                            <button
                                                type="button"
                                                className="lv-btn lv-btn-ghost lv-btn-sm"
                                                onClick={() => apriModifica(b)}
                                            >
                                                Modifica
                                            </button>
                                            <button
                                                type="button"
                                                className="lv-btn lv-btn-ghost lv-btn-sm lv-btn-danger-ghost"
                                                onClick={() => setDaEliminare(b)}
                                            >
                                                Elimina
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <FormModal
                show={Boolean(form)}
                title={inModifica ? `Blocco «${inModifica.titolo}»` : 'Nuovo blocco'}
                onClose={() => setForm(null)}
                onSubmit={salva}
                saving={saving}
                submitLabel={inModifica ? 'Salva blocco' : 'Crea blocco'}
            >
                {form && (
                    <>
                        <Field label="Titolo" required hint="Es. «GAS», «Natale», «Manutenzione impianto».">
                            <input className="lv-input" value={form.titolo} onChange={e => aggiorna('titolo', e.target.value)} />
                        </Field>
                        <Field
                            label="Tipo"
                            hint={form.tipo === 'festivita'
                                ? 'Non chiude la sala: gli amministratori la prenotano sempre, i condomini da 15 giorni prima.'
                                : 'Chiude la sala negli orari indicati.'}
                        >
                            <Select value={form.tipo} onChange={v => aggiorna('tipo', v)} options={TIPI} />
                        </Field>
                        <Field label="Ricorrenza">
                            <Select
                                value={form.ricorrenza}
                                onChange={v => aggiorna('ricorrenza', v)}
                                options={RICORRENZE}
                            />
                        </Field>
                        {form.ricorrenza === 'settimanale' && (
                            <Field label="Giorno della settimana" required>
                                <Select
                                    value={form.giorno_settimana}
                                    onChange={v => aggiorna('giorno_settimana', v)}
                                    options={GIORNI.map((g, i) => ({ value: String(i + 1), label: g }))}
                                />
                            </Field>
                        )}
                        <Field
                            label="Data di inizio"
                            required
                            hint={form.ricorrenza === 'nessuna' ? 'Giorno bloccato.' : 'Inizio validità della ricorrenza.'}
                        >
                            <input
                                type="date"
                                className="lv-input"
                                value={form.data_inizio}
                                onChange={e => aggiorna('data_inizio', e.target.value)}
                            />
                        </Field>
                        <Field label="Data di fine" hint="Vuoto = senza scadenza.">
                            <input
                                type="date"
                                className="lv-input"
                                value={form.data_fine}
                                onChange={e => aggiorna('data_fine', e.target.value)}
                            />
                        </Field>
                        <Field label="Ora di inizio" hint="Vuoto = blocca l'intera giornata.">
                            <input
                                type="time"
                                className="lv-input"
                                value={form.ora_inizio}
                                onChange={e => aggiorna('ora_inizio', e.target.value)}
                            />
                        </Field>
                        <Field label="Ora di fine">
                            <input
                                type="time"
                                className="lv-input"
                                value={form.ora_fine}
                                onChange={e => aggiorna('ora_fine', e.target.value)}
                            />
                        </Field>
                        <Field label="Stato">
                            <Select
                                value={form.attivo}
                                onChange={v => aggiorna('attivo', v)}
                                options={[
                                    { value: '1', label: 'Attivo' },
                                    { value: '0', label: 'Non attivo' }
                                ]}
                            />
                        </Field>
                        <Field label="Note" wide>
                            <textarea
                                className="lv-input"
                                rows={2}
                                value={form.note}
                                onChange={e => aggiorna('note', e.target.value)}
                            />
                        </Field>
                    </>
                )}
            </FormModal>

            <ConfirmModal
                show={Boolean(daEliminare)}
                title="Eliminare il blocco?"
                message={daEliminare ? `«${daEliminare.titolo}» tornerà disponibile per le prenotazioni.` : ''}
                confirmLabel="Elimina"
                danger
                working={working}
                onConfirm={elimina}
                onClose={() => setDaEliminare(null)}
            />
        </div>
    );
}
